// Arrowheads are filled outlines: the visible apex is exactly `tip`, independent of
// stroke joins, miter limits, line caps, and device-pixel alignment. Open/half heads
// retain their open silhouettes without relying on two independently stroked barbs.
export interface ArrowPoint {
  readonly x: number;
  readonly y: number;
}
export type ArrowHeadStyle = 'solid' | 'open' | 'half' | 'swallowtail';
export const ARROW_HEAD_LENGTH_IN_STROKES = 6;
export const ARROW_HEAD_INCLUDED_ANGLE_DEGREES = 36;
export const OPEN_ARROW_HEAD_WIDTH_SCALE = 1.25;
export const DEFAULT_ARROW_STROKE_WIDTH = 1.75;

export function arrowHeadDimensions(strokeWidth: number) {
  const width = Number.isFinite(strokeWidth) && strokeWidth > 0 ? strokeWidth : DEFAULT_ARROW_STROKE_WIDTH;
  const angle = (ARROW_HEAD_INCLUDED_ANGLE_DEGREES * Math.PI) / 360;
  return {
    length: width * ARROW_HEAD_LENGTH_IN_STROKES,
    halfWidth: width * ARROW_HEAD_LENGTH_IN_STROKES * Math.tan(angle),
    width,
    angle
  };
}

export function arrowHead(
  tip: ArrowPoint,
  direction: ArrowPoint,
  style: ArrowHeadStyle,
  strokeWidth = DEFAULT_ARROW_STROKE_WIDTH,
  side: 1 | -1 = -1,
  outlineWidth = strokeWidth
) {
  const magnitude = Math.hypot(direction.x, direction.y) || 1;
  const ux = magnitude === 1 && direction.x === 0 && direction.y === 0 ? 1 : direction.x / magnitude;
  const uy = direction.y / magnitude;
  const dimensions = arrowHeadDimensions(strokeWidth);
  const length = dimensions.length;
  const halfWidth = dimensions.halfWidth * (style === 'open' ? OPEN_ARROW_HEAD_WIDTH_SCALE : 1);
  const angle = Math.atan2(halfWidth, length);
  const width = Math.min(outlineWidth, halfWidth * 0.9);
  const point = (back: number, across: number): ArrowPoint => ({
    x: tip.x - ux * back - uy * across,
    y: tip.y - uy * back + ux * across
  });
  const innerTip = width / Math.sin(angle);
  const innerWidth = halfWidth - width / Math.cos(angle);
  let points: readonly ArrowPoint[];
  if (style === 'open') {
    points = [
      point(length, -halfWidth),
      tip,
      point(length, halfWidth),
      point(length, innerWidth),
      point(innerTip, 0),
      point(length, -innerWidth)
    ];
  } else if (style === 'half') {
    points = [
      point(length, (-side * width) / 2),
      tip,
      point(length, side * halfWidth),
      point(length, side * innerWidth),
      point(innerTip, 0),
      point(length, (side * width) / 2)
    ];
  } else if (style === 'swallowtail') {
    points = [point(length, -halfWidth), tip, point(length, halfWidth), point(length * 0.62, 0)];
  } else {
    points = [tip, point(length, halfWidth), point(length, -halfWidth)];
  }
  return {
    primitive: { t: 'polygon' as const, filled: true, head: style, points },
    base: point(style === 'swallowtail' ? length * 0.5 : length, 0),
    // For twin retrosynthetic rails, stop inside the corresponding outline arm.
    railEnd: (across: number) => point((Math.abs(across) + width / (2 * Math.cos(angle))) / Math.tan(angle), across)
  };
}
